// src/hooks/useJobPolling.js
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchJobStatus } from "../helpers/eirna_backend_helper";

export const useJobPolling = () => {
  const [plotLoading, setPlotLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const navigate = useNavigate();

  const pollTaskStatus = useCallback(
    async (id, onSuccess, onError, pollInterval = 2000) => {
      try {
        const response = await fetchJobStatus(id);
        const data = response?.data || response || {};

        if (["SUCCESS", "FAILURE"].includes(data.state)) {
          setPlotLoading(false);
          setTaskId(null);

          if (data.state === "SUCCESS") {
            if (data.result?.message === "user logged out") {
              navigate("/login");
              return;
            }
            if (
              data.result?.message &&
              !data.result?.table &&
              !data.result?.plot
            ) {
              const errorMsg = data.result.message;
              onError(errorMsg);
              toast.error(errorMsg);
            } else {
              const result =
                data.result?.table || data.result?.plot || data.result;
              onSuccess(result);
              toast.success("Analysis completed successfully");
            }
          } else {
            const errorMsg = `Job failed. Task ID: ${id}`;
            onError(errorMsg);
            toast.error(errorMsg);
          }
        } else {
          // Continue polling
          setTimeout(
            () => pollTaskStatus(id, onSuccess, onError, pollInterval),
            pollInterval
          );
        }
      } catch (err) {
        console.error("Polling error:", err);
        const errorMsg = `Unable to fetch job status. Task ID: ${id}`;
        onError(errorMsg);
        toast.error(errorMsg);
        setPlotLoading(false);
        setTaskId(null);
      }
    },
    [navigate]
  );

  const startJob = useCallback(
    async (jobFunction, payload, onSuccess, onError) => {
      try {
        setPlotLoading(true);
        setTaskId(null);

        const response = await jobFunction(payload);
        const data = response?.data || response || {};

        if (data.task_id) {
          setTaskId(data.task_id);
          toast.info(`Job started (ID: ${data.task_id})`);
          pollTaskStatus(data.task_id, onSuccess, onError);
        } else if (data.message) {
          if (data.message === "user logged out") {
            navigate("/login");
            return;
          }
          onError(data.message);
          toast.error(data.message);
          setPlotLoading(false);
        } else if (data.table || data.plot) {
          onSuccess(data.table || data.plot);
          toast.success("Analysis completed successfully");
          setPlotLoading(false);
        } else {
          const errorMsg = "No valid response received";
          onError(errorMsg);
          toast.error(errorMsg);
          setPlotLoading(false);
        }
      } catch (error) {
        console.error("Job submission error:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "Server error";
        onError(errorMsg);
        toast.error(errorMsg);
        setPlotLoading(false);
      }
    },
    [navigate, pollTaskStatus]
  );

  return {
    plotLoading,
    taskId,
    startJob,
    setPlotLoading: (loading) => setPlotLoading(loading),
  };
};
